/**
 * Constants for pre-launch landing page
 */

export const PRE_LAUNCH_CONTENT = {
  title: "Find a Feeling",
  subtitle: "Discover feelings through your body, one step at a time",

  // Main sections
  whatAndWhy: {
    heading: "What is it?",
    description:
      "A calming web app that helps children and adults name what they feel, starting with body sensations, not words.",
    problem:
      'Most emotional tools assume users already know words like "frustrated" or "disappointed".',
    solution: "Find a Feeling bridges that gap.",
  },

  approach: {
    steps: [
      { label: "Body", color: "text-approach-body" },
      { label: "Emotion", color: "text-approach-emotion" },
      { label: "Understanding", color: "text-approach-understanding" },
      { label: "Communication", color: "text-approach-communication" },
    ],
    arrowColor: "text-approach-arrow", // border-default color
  },

  audience: {
    heading: "Who is it for?",
    groups: ["Parents", "Teachers", "Therapists", "Kids 6-11"],
  },

  // Survey Section
  survey: {
    heading: "Help shape the product!",
    description:
      "I'm surveying parents of elementary-age children to better understand common challenges and needs with big emotions and self-regulation.",
    timeEstimate: "The survey takes about 2 minutes to complete.",
    benefits:
      "Your input helps ensure Find a Feeling addresses real family needs. Plus, survey participants get early access when we launch!",
    thankYou: "Your perspective helps make Find a Feeling more helpful for families like yours.",
    buttonText: "Take 2-Minute Survey",
    url: "https://forms.gle/CFGVeGisrYjaKy1C9", // Google Forms survey link
  },

  // Email CTA
  cta: {
    heading: "Get launch updates & early access",
    description: "Be the first to try Find a Feeling when it launches.",
    placeholder: "Your email",
    buttonText: "Join the Waitlist",
    submittingText: "Submitting...",
  },

  success: {
    icon: "✓",
    title: "You're on the list!",
    message: "You'll receive launch updates and early access. We'll be in touch soon!",
  },

  footer: {
    question: "Questions?",
    linkText: "Get in touch",
    email: "hello.findafeeling@gmail.com",
    contactDescription: "Send us a message via our contact form",
  },

  privacy: "No spam. Only updates. We respect your privacy and won't share your email.",
} as const;

// Logo configuration
export const LOGO_IMAGES = [
  { src: "/faf - kids icon outline.png", alt: "Explorer mode for kids", size: "md" },
  { src: "/faf  - adult icon outline.png", alt: "Adult mode", size: "lg" },
  { src: "/faf - admin icon outline.png", alt: "Admin mode", size: "md" },
] as const;

// Shared validation patterns
export const EMAIL_REGEX = /^[^@]+@[^@]+\.[^@]+$/;
export const EMAIL_ERROR_MESSAGE = "Enter a valid email";

// API configuration
export const FORMSUBMIT_EMAIL = "hello.findafeeling@gmail.com";
export const FORMSUBMIT_SUBJECT_PRELAUNCH = "Find-a-Feeling Pre-Launch Signup";
export const FORMSUBMIT_SUBJECT_CONTACT = "Find-a-Feeling Contact Message";

// Toast messages for consistency
export const TOAST_MESSAGES = {
  contact: {
    success: "Thank you for your message! We will get back to you shortly.",
    error: "There was an error submitting your message. Please try again.",
  },
  prelaunch: {
    error: "Something went wrong. Please try again.",
  },
} as const;

// Dialog styling constants
// For color formulas and accessibility info, see: /src/styles/BRAND_COLORS.md
export const DIALOG_STYLES = {
  mintGlow: "0 0 50px rgba(138, 191, 170, 0.25), 0 0 20px rgba(138, 191, 170, 0.12)",
  coral: "#E08F87", // brand-accent-coral - RGB: 224, 143, 135
} as const;

// Contact dialog content
export const CONTACT_DIALOG_CONTENT = {
  title: "Contact Us",
  description: "Please, be in touch!",
  emailLabel: "Email",
  messageLabel: "Message",
  submitButton: {
    default: "Send Message",
    submitting: "Sending...",
  },
} as const;
